import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import flagsmith from '@flagsmith/flagsmith';

type FlagsMap = Record<string, boolean>;

export interface FeatureFlagsContextValue {
  flags: FlagsMap;
  isLoading: boolean;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: {},
  isLoading: true,
  isEnabled: () => false,
  refresh: async () => { },
});

interface FeatureFlagsProviderProps {
  children: ReactNode;
  organizationId?: string;
  userId?: string;
}

export function FeatureFlagsProvider({
  children,
  organizationId,
  userId,
}: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<FlagsMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const isInitialized = React.useRef(false);

  const loadFlags = useCallback(async () => {
    try {
      setIsLoading(true);

      const identity = organizationId
        ? `org_${organizationId}`
        : userId
          ? `user_${userId}`
          : undefined;

      console.log('=== FeatureFlags Debug ===');
      console.log('organizationId:', organizationId);
      console.log('userId:', userId);
      console.log('identity:', identity);

      if (!isInitialized.current) {
        await flagsmith.init({
          environmentID: import.meta.env.VITE_FLAGSMITH_CLIENT_KEY,
          identity,
          onChange: () => {
            const allFlags = flagsmith.getAllFlags();
            const result: FlagsMap = {};
            Object.keys(allFlags).forEach((key) => {
              result[key] = flagsmith.hasFeature(key);
            });
            setFlags(result);
          },
        });
        isInitialized.current = true;
      } else {
        await flagsmith.getFlags();
      }

      const allFlags = flagsmith.getAllFlags();
      const result: FlagsMap = {};
      Object.keys(allFlags).forEach((key) => {
        result[key] = flagsmith.hasFeature(key);
      });
      setFlags(result);
    } catch (error) {
      console.error('[FeatureFlags] Error loading flags:', error);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, userId]);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/feature-flags/context', {
        credentials: 'include',
      });
      const data = await res.json();
      const result: FlagsMap = {};
      Object.keys(data).forEach((key) => {
        result[key] = data[key];
      });
      setFlags(result);
    } catch (error) {
      console.error('[FeatureFlags] Error refreshing flags:', error);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      return flags[key] ?? false;
    },
    [flags],
  );

  return (
    <FeatureFlagsContext.Provider
      value={{ flags, isLoading, isEnabled, refresh }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext);
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

export function useFeatureFlag(key: string): boolean {
  const { isEnabled } = useContext(FeatureFlagsContext);
  return isEnabled(key);
}